import { z } from "zod";

export const buildingPlanTierSchema = z.enum(["free", "starter", "growing", "scale"]);
export const billingPlanSchema = z.object({
  code: buildingPlanTierSchema,
  name: z.string(),
  rate_cents: z.number().int().nonnegative(),
  storage_limit_bytes: z.number().int().nonnegative().nullable(),
  max_projects: z.number().int().nonnegative().nullable(),
  chat_questions_per_month: z.number().int().nonnegative().nullable(),
  max_pm_issues: z.number().int().nonnegative().nullable(),
});
export const billingPlanCatalogSchema = z
  .array(billingPlanSchema)
  .refine(
    (plans) =>
      plans.length === buildingPlanTierSchema.options.length &&
      new Set(plans.map((plan) => plan.code)).size === buildingPlanTierSchema.options.length,
    "The building plan catalog must contain each supported plan once"
  );
export const buildingSubscriptionSchema = z.object({
  building_id: z.number().int(),
  plan: billingPlanSchema,
  requested_tier: buildingPlanTierSchema.nullable(),
  usage: z.object({
    projects: z.number().int().nonnegative(),
    pm_issues: z.number().int().nonnegative(),
    chat_questions: z.number().int().nonnegative(),
  }),
  chat_period_start: z.string(),
  chat_period_end: z.string(),
  can_manage: z.boolean(),
  activation_pending: z.boolean().optional(),
  billing_enabled: z.boolean().optional(),
  billing_exempt: z.boolean().optional(),
});
export const buildingPlanChangeSchema = z.object({
  building_id: z.number().int(),
  tier: buildingPlanTierSchema,
  requested_tier: buildingPlanTierSchema.nullable(),
  status: z.enum(["applied", "pending"]),
  proration_behavior: z.literal("none"),
  billing_effective: z.literal("next_renewal"),
});
export type BuildingPlanTier = z.infer<typeof buildingPlanTierSchema>;
export type BillingPlan = z.infer<typeof billingPlanSchema>;
export type BuildingSubscription = z.infer<typeof buildingSubscriptionSchema>;

/**
 * The invoice a banner names.
 *
 * `due_on` is NULLABLE, and that is the whole point of the automatic-collection
 * work: Stripe only fills `due_date` on `collection_method=send_invoice`
 * invoices (docs.stripe.com/api/invoices/object), so every invoice of a card or
 * SEPA organisation carries none. The overdue signal is now selected on a
 * declined charge as well as a passed due date, so requiring a string here made
 * the whole context unparseable the moment a card was refused — which blanks
 * all three billing pages and withholds every mutation app-wide.
 */
const invoiceSignalSchema = z.object({
  id: z.number(),
  invoice_number: z.string(),
  due_on: z.string().nullable().default(null),
});

/**
 * How the organisation pays, card first because it is the recommended default.
 *
 * The first two are charged automatically by Stripe (`charge_automatically`);
 * the two bank-transfer values are invoiced with a 14 day term
 * (`send_invoice`). `isAutomaticPaymentMethod` is the only place that split is
 * decided — the picker, the on-file block and the summary subtext all read it.
 */
export const paymentMethodSchema = z.enum([
  "card",
  "sepa_debit",
  "bank_transfer_email",
  "bank_transfer_e_invoice",
]);

/** The method types Stripe can hold on file for automatic collection. */
export const paymentMethodTypeSchema = z.enum(["card", "sepa_debit"]);

export function isAutomaticPaymentMethod(method: string | null | undefined): boolean {
  return method === "card" || method === "sepa_debit";
}

/**
 * The payment method Stripe has attached, as the mirror recorded it.
 *
 * `label` is a Stripe-derived identifier ("Visa •••• 4242", "SEPA •••• 3000"),
 * not prose: it carries a brand name and masked digits and reads the same in
 * every language, so it is rendered verbatim inside a localised sentence.
 */
const paymentMethodOnFileSchema = z.object({
  type: paymentMethodTypeSchema,
  label: z.string(),
});

/** The invoice whose automatic charge was declined, if one is still open. */
const failedPaymentInvoiceSchema = z.object({
  id: z.number().int(),
  invoice_number: z.string(),
  failed_on: z.string(),
});

// Every field here is nullable because the context RPC withholds the whole
// block from a subcontractor — it returns the state flags they need for the
// write guards, but not the organisation's invoicing identity. Requiring
// payment_method would throw on parse for every subcontractor, and a thrown
// context reads as "billing status unknown", which blocks every mutation in
// the app for them.
const billingDetailsSchema = z.object({
  company_name: z.string().nullable(),
  registry_code: z.string().nullable(),
  vat_number: z.string().nullable(),
  legal_address: z.string().nullable(),
  invoice_email: z.string().nullable(),
  purchase_order_number: z.string().nullable(),
  payment_method: paymentMethodSchema.nullable(),
});

export const billingContextSchema = z.object({
  org_id: z.string(),
  is_org_owner: z.boolean(),
  allows_legacy_building_creation: z.boolean().optional(),
  is_pilot: z.boolean(),
  pilot_until: z.string().nullable(),
  details_missing: z.boolean(),
  paid_details_missing: z.boolean().optional(),
  paid_payment_method_missing: z.boolean().optional(),
  is_read_only: z.boolean(),
  overdue_invoice: invoiceSignalSchema.nullable(),
  read_only_invoice: invoiceSignalSchema.nullable(),
  list_rate_cents: z.number().int(),
  negotiated_rate_cents: z.number().int().nullable(),
  negotiated_rate_valid_until: z.string().nullable(),
  billing_contact_name: z.string().nullable(),
  effective_rate_cents: z.number().int(),
  currency: z.string(),
  details: billingDetailsSchema,
  /**
   * The card / SEPA fields, every one defaulted.
   *
   * `get_organisation_billing_context` gains them in the same release as this
   * page, but the app and the database deploy separately and a PR preview
   * points at whichever branch database exists. A required field here would
   * make an older RPC unparseable, and an unparseable context is not a missing
   * banner — it blanks all three billing pages and withholds CREATE_BUILDINGS
   * org-wide. The defaults degrade to "bank transfer, nothing on file", which
   * is exactly what an organisation on the older schema is.
   */
  collection_method: z.enum(["charge_automatically", "send_invoice"]).nullable().default(null),
  payment_method_missing: z.boolean().default(false),
  payment_method_on_file: paymentMethodOnFileSchema.nullable().default(null),
  failed_payment_invoice: failedPaymentInvoiceSchema.nullable().default(null),
});

const billedBuildingSchema = z.object({
  building_id: z.number().int(),
  building_name: z.string(),
  address: z.string().nullable(),
  cadastral_code: z.string().nullable(),
  service_start: z.string(),
  service_end: z.string(),
  billable_days: z.number().int(),
  period_days: z.number().int(),
  rate_cents: z.number().int(),
  renewal_rate_cents: z.number().int().optional(),
  amount_cents: z.number().int(),
  removal_requested_on: z.string().nullable(),
  subscription_tier: buildingPlanTierSchema.optional(),
  requested_subscription_tier: buildingPlanTierSchema.nullable().optional(),
  subscription_plan_billing_enabled: z.boolean().optional(),
});

const removedBuildingSchema = z.object({
  building_id: z.number().int(),
  building_name: z.string(),
  address: z.string().nullable(),
  removed_on: z.string(),
  usable_until: z.string(),
  deletes_on: z.string(),
  can_restore: z.boolean(),
});

export const billingOverviewSchema = z.object({
  context: billingContextSchema,
  period_start: z.string(),
  period_end: z.string(),
  closes_in_days: z.number().int(),
  building_count: z.number().int(),
  added_this_period_count: z.number().int(),
  estimated_total_cents: z.number().int(),
  renewal_total_cents: z.number().int().optional(),
  /**
   * The masked identifier the next invoice will be charged to
   * ("Visa •••• 4242"), or null when the organisation is invoiced instead.
   *
   * The RPC deliberately supplies the VALUE and not a sentence: "charged to …"
   * versus "sent by email" is composed in the app, because this page is
   * bilingual and prose assembled in Postgres could only ever be English.
   */
  next_invoice_method_label: z.string().nullable().default(null),
  buildings: z.array(billedBuildingSchema),
  removed_buildings: z.array(removedBuildingSchema),
  add_building_quote: z.object({
    charge_timing: z.enum(["immediate", "next_invoice"]).optional(),
    charge_type: z.enum(["full_month", "prorated"]).optional(),
    remaining_seconds: z.number().int().nonnegative().optional(),
    period_seconds: z.number().int().positive().optional(),
    monthly_rate_cents: z.number().int(),
    billable_days: z.number().int(),
    period_days: z.number().int(),
    amount_cents: z.number().int(),
  }),
});

export const billingInvoiceSchema = z.object({
  id: z.number().int(),
  invoice_number: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  issued_on: z.string(),
  /**
   * Null on every automatically collected invoice — see `invoiceSignalSchema`.
   * The history no longer filters those rows out, so this is the shape a
   * card-paying organisation's own invoice list actually has.
   */
  due_on: z.string().nullable().default(null),
  // billing_invoices.status is a plain text column, not an enum type. Parsing it
  // as a closed set would make one unexpected status (void, credited, …) throw
  // for the whole array and blank the invoices page; the presentation map falls
  // back to the raw value for a status it does not know.
  status: z.string(),
  amount_cents: z.number().int(),
  subtotal_cents: z.number().int(),
  building_count: z.number().int(),
  currency: z.string(),
  /** Path inside the private `billing-invoices` bucket; null until archived. */
  pdf_storage_path: z.string().nullable(),
  /**
   * Stripe's hosted invoice page. Used only when the archived PDF is not there
   * yet — it is an unauthenticated link, so the signed bucket URL comes first.
   * `get_organisation_billing_invoices` DOES project it — that is what makes
   * the fallback usable between finalisation and the PDF landing in the bucket.
   * The default survives so an older deployment of the RPC, which does not,
   * still parses: an absent fallback must degrade to "no download yet", never
   * to an unparseable invoice list.
   *
   * Anything that is not an https page is dropped here rather than at the two
   * call sites: this value is handed to `window.open` and to `link.href`, which
   * makes any other scheme a navigation primitive supplied by a backend column.
   * Dropping it at parse keeps the download button honestly disabled instead of
   * silently doing nothing.
   */
  hosted_invoice_url: z
    .string()
    .nullable()
    .default(null)
    .transform((value) => (value && /^https:\/\//i.test(value) ? value : null)),
  /**
   * When Stripe last failed to charge this invoice automatically, and when it
   * was settled. Defaulted for the same reason as the context's new fields.
   */
  payment_failed_on: z.string().nullable().default(null),
  paid_on: z.string().nullable().default(null),
});

export const billingInvoicesSchema = z.array(billingInvoiceSchema);

export const billingBuildingResultSchema = z.object({
  building_id: z.number().int(),
  status: z.enum(["applied", "pending"]).optional(),
  tier: buildingPlanTierSchema.optional(),
  plan_change_id: z.string().uuid().nullable().optional(),
  activation_pending: z.boolean().optional(),
  creation_request_id: z.string().uuid().nullable().optional(),
});

/**
 * The hosted Stripe Checkout URL `setup_payment_method` answers with.
 *
 * The value is handed to `window.location.assign`, which makes it a navigation
 * primitive supplied by a backend field — so anything that is not an https URL
 * is rejected at parse, exactly as `hosted_invoice_url` is. Verified against
 * docs.stripe.com/api/checkout/sessions/object: `url` is the hosted-page
 * redirect target and is only present while the session is active.
 */
export const billingSetupSessionSchema = z.object({
  url: z.string().regex(/^https:\/\//i, "The billing setup URL must be an https URL"),
});

export type BillingContext = z.infer<typeof billingContextSchema>;
export type BillingDetails = z.infer<typeof billingDetailsSchema>;
export type BillingOverview = z.infer<typeof billingOverviewSchema>;
export type BilledBuilding = z.infer<typeof billedBuildingSchema>;
export type RemovedBuilding = z.infer<typeof removedBuildingSchema>;
export type BillingInvoice = z.infer<typeof billingInvoiceSchema>;
export type BillingPaymentMethod = z.infer<typeof paymentMethodSchema>;
export type BillingPaymentMethodOnFile = z.infer<typeof paymentMethodOnFileSchema>;

export interface BillingDetailsInput {
  companyName: string;
  registryCode: string;
  vatNumber: string;
  legalAddress: string;
  invoiceEmail: string;
  purchaseOrderNumber: string;
  paymentMethod: BillingPaymentMethod;
}
